import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Drawer, List, ListItem, ListItemText,
  IconButton, Alert, CircularProgress, Tooltip, Divider, Chip,
  Collapse
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { listBackups, restoreBackup, deleteBackup } from '../utils/api';

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  return `${(bytes / 1024).toFixed(1)} Ko`;
};

const formatDate = (mtime) => {
  const d = new Date(mtime * 1000);
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const SCOPE_LABELS = {
  categories: 'Catégorie',
  menus: 'Menus',
  'menus-picker': 'Menus Picker',
};

const FIELD_LABELS = {
  nom: 'Nom', prix: 'Prix', description: 'Description',
  ref: 'Réf', 'sous-categ': 'Sous-catégorie', precision: 'Précision',
  image: 'Image', options: 'Items du menu',
};

const typeConfig = {
  added:    { icon: <AddCircleOutlineIcon sx={{ fontSize: 14 }} />, color: 'success.main', label: 'Ajouté' },
  modified: { icon: <EditIcon sx={{ fontSize: 14 }} />,            color: 'warning.main', label: 'Modifié' },
  removed:  { icon: <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />, color: 'error.main', label: 'Supprimé' },
};

const SummaryChips = ({ summary }) => {
  if (!summary) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
      {summary.added   > 0 && <Chip icon={typeConfig.added.icon}    label={`+${summary.added}`}    size="small" color="success" variant="outlined" />}
      {summary.modified > 0 && <Chip icon={typeConfig.modified.icon} label={`~${summary.modified}`} size="small" color="warning" variant="outlined" />}
      {summary.removed > 0 && <Chip icon={typeConfig.removed.icon}  label={`-${summary.removed}`}  size="small" color="error"   variant="outlined" />}
      {summary.added === 0 && summary.modified === 0 && summary.removed === 0 && (
        <Chip label="Aucun changement" size="small" variant="outlined" />
      )}
    </Box>
  );
};

// Produit une ligne par changement : "S1 : prix 6.5 → 6.4", "+ C10 – POULET", "- 15 – SUSHI SAUMON"
const flattenChanges = (changes) => {
  const lines = [];
  for (const c of changes) {
    const ref  = c.ref  || '';
    const cat  = c.category ? `[${c.category}] ` : `[${SCOPE_LABELS[c.scope] || c.scope}] `;

    if (c.type === 'added') {
      lines.push({ type: 'added',   text: `${cat}${ref}${ref ? ' – ' : ''}${c.item}` });
    } else if (c.type === 'removed') {
      lines.push({ type: 'removed', text: `${cat}${ref}${ref ? ' – ' : ''}${c.item}` });
    } else if (c.type === 'modified') {
      for (const f of (c.fields || [])) {
        const label = FIELD_LABELS[f.field] || f.field;
        if (f.field === 'options') {
          lines.push({ type: 'modified', text: `${cat}${ref} : ${label} modifiés` });
        } else {
          lines.push({ type: 'modified', text: `${cat}${ref} : ${label} ${f.from} → ${f.to}` });
        }
      }
    }
  }
  return lines;
};

const ChangeDetail = ({ changes }) => {
  const lines = flattenChanges(changes);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {lines.map((line, i) => {
        const cfg = typeConfig[line.type] || {};
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
            pl: 1, borderLeft: '2px solid', borderColor: cfg.color || 'grey.300' }}>
            <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon}</Box>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
              {line.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const BackupEntry = ({ backup, onRestore, onDelete, loading, confirming, setConfirming }) => {
  const [expanded, setExpanded] = useState(false);
  const changes = backup.diff?.changes || [];
  const summary = backup.diff?.summary || null;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{
        border: '1px solid',
        borderColor: confirming === backup.file ? 'warning.main' : 'divider',
        borderRadius: 1,
        p: 1.5,
        bgcolor: confirming === backup.file ? 'warning.lighter' : 'background.paper',
      }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {formatDate(backup.mtime)}
            </Typography>
            <Typography variant="caption" color="text.secondary">{formatSize(backup.size)}</Typography>
            <SummaryChips summary={summary} />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
            {changes.length > 0 && (
              <Tooltip title="Voir les modifications">
                <IconButton size="small" onClick={() => setExpanded(v => !v)}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={confirming === backup.file ? 'Confirmer la restauration' : 'Restaurer cette version'}>
              <IconButton
                size="small"
                color={confirming === backup.file ? 'warning' : 'primary'}
                onClick={() => onRestore(backup.file)}
                disabled={loading}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer ce backup">
              <IconButton size="small" color="error" onClick={() => onDelete(backup.file)} disabled={loading}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {confirming === backup.file && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
            Cliquer à nouveau sur pour confirmer la restauration
          </Typography>
        )}

        {/* Détail des changements */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 1.5 }}>
            <ChangeDetail changes={changes} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

const BackupManager = ({ user, onRestore }) => {
  const [open, setOpen] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listBackups();
      setBackups(list);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleRestore = async (file) => {
    if (confirming !== file) { setConfirming(file); return; }
    setConfirming(null);
    setLoading(true);
    setMessage(null);
    try {
      await restoreBackup(file, user);
      setMessage({ type: 'success', text: 'Version restaurée. Un backup de sécurité a été créé.' });
      await load();
      if (onRestore) onRestore();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    setLoading(false);
  };

  const handleDelete = async (file) => {
    setLoading(true);
    setMessage(null);
    try {
      await deleteBackup(file, user);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    setLoading(false);
  };

  const handleClose = () => { setOpen(false); setMessage(null); setConfirming(null); };

  return (
    <>
      <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => setOpen(true)}>
        Historique
      </Button>

      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box sx={{ width: 420, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" gutterBottom>Historique des sauvegardes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {backups.length} version{backups.length !== 1 ? 's' : ''} disponible{backups.length !== 1 ? 's' : ''} (max 30)
          </Typography>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          {loading && <Box sx={{ textAlign: 'center', my: 3 }}><CircularProgress size={28} /></Box>}

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {backups.map((b, idx) => (
              <React.Fragment key={b.file}>
                {idx === 0 && (
                  <Chip label="Version actuelle (avant dernière sauvegarde)" size="small" color="primary" sx={{ mb: 1 }} />
                )}
                <BackupEntry
                  backup={b}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                  loading={loading}
                  confirming={confirming}
                  setConfirming={setConfirming}
                />
              </React.Fragment>
            ))}

            {!loading && backups.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                Aucune sauvegarde disponible.
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Button onClick={handleClose}>Fermer</Button>
        </Box>
      </Drawer>
    </>
  );
};

export default BackupManager;
